'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn, Flex, LabelField, LabelFieldError, RadioFieldSize } from '@bridebook/aura';
import { ComponentPropsWithoutRef, ElementRef, forwardRef, useId, useMemo } from 'react';
import { RadioGroupContext } from './radio-group-context';

type RadioGroupProps = ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
  error?: boolean | string;
  size?: RadioFieldSize;
};

export const RadioGroup = forwardRef<ElementRef<typeof RadioGroupPrimitive.Root>, RadioGroupProps>(
  ({ error, id: idProps, className, size = 'standard', ...props }, ref) => {
    const defaultId = useId();
    const id = idProps ?? defaultId;
    const errorId = `${id}-error`;

    const hasErrorMessage = typeof error === 'string';
    const ariaDescribedby = hasErrorMessage ? errorId : undefined;

    const contextValue = useMemo(
      () => ({
        size,
        error,
      }),
      [size, error],
    );

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <Flex className="flex-col gap-4">
          <RadioGroupPrimitive.Root
            ref={ref}
            data-slot="radio-group"
            className={cn('group grid gap-4', className)}
            aria-invalid={!!error}
            aria-describedby={ariaDescribedby}
            {...props}
          />
          {typeof error === 'string' && (
            <LabelField size={size} errorId={errorId}>
              <LabelFieldError>{error}</LabelFieldError>
            </LabelField>
          )}
        </Flex>
      </RadioGroupContext.Provider>
    );
  },
);
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
